import { Theme, ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { colors } from '@/styles/theme/colors';

type CustomColors = {
  'blue-ribbon': {
    500: string;
    900: string;
  };
  'mint-green': {
    50: string;
    100: string;
    200: string;
    250: string;
    300: string;
    400: string;
    600: string;
    700: string;
    800: string;
    950: string;
  };
  orange: {
    50: string;
    100: string;
    200: string;
    300: string;
    350: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    850: string;
    900: string;
  };
  gray: {
    50: string;
    100: string;
  };
  customPrimary: {
    focusVisible: string;
  };
};

declare module '@mui/material' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    xsm: true;
    md: true;
    xmd: true;
    lg: true;
    xl: true;
  }
  interface Palette extends CustomColors {}
  interface PaletteOptions extends CustomColors {}
}

declare module '@mui/material/styles' {
  interface PaletteColor {
    states: {
      outiliedBorder: string;
      selected: string;
      focusVisible: string;
    };
  }
  interface SimplePaletteColorOptions {
    states: {
      outiliedBorder: string;
      selected: string;
      focusVisible: string;
    };
  }
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const theme: Theme = useMemo(
    () =>
      createTheme({
        components: {
          MuiButton: {
            variants: [
              {
                props: { variant: 'contained' },
                style: {
                  backgroundColor: colors.primary.main,
                  padding: '6px 16px',
                  minWidth: 'auto',
                  color: colors.text.primary,
                  ['&:hover']: {
                    backgroundColor: colors.primary.main,
                    filter: 'brightness(0.9)',
                  },
                },
              },
              {
                props: { variant: 'outlined' },
                style: {
                  borderColor: colors.primary.light,
                  padding: '6px 16px',
                  minWidth: 'auto',
                  color: colors['mint-green'][800],
                  ['&:hover']: {
                    borderColor: colors.primary.light,
                    backgroundColor: colors['mint-green'][100],
                  },
                  ['&:focus']: {
                    borderColor: colors.primary.states.outiliedBorder,
                    boxShadow: `0 0 0 4px ${colors.primary.states.focusVisible}`,
                  },
                },
              },
            ],
          },
          MuiIconButton: {
            variants: [
              {
                props: {},
                style: {
                  minWidth: 'auto',
                  color: colors.common.white,
                },
              },
            ],
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                '&.Mui-checked': {
                  backgroundColor: 'primary.main',
                },
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              endAdornment: {
                color: 'action.active',
              },
            },
          },
          MuiSvgIcon: {
            styleOverrides: {
              colorAction: {
                color: 'action.active',
              },
            },
          },
          MuiCircularProgress: {
            styleOverrides: {
              root: {
                color: 'action.active',
              },
            },
          },
        },
        palette: {
          primary: {
            main: colors.primary.main,
            light: colors.primary.light,
            dark: colors.primary.dark,
            states: {
              outiliedBorder: colors.primary.states.outiliedBorder,
              selected: colors.primary.states.selected,
              focusVisible: colors.primary.states.focusVisible,
            },
          },
          background: {
            default: colors.background,
          },
          text: {
            primary: colors.text.primary,
            secondary: colors.text.secondary,
          },
          'blue-ribbon': {
            500: colors['blue-ribbon'][500],
            900: colors['blue-ribbon'][900],
          },
          'mint-green': {
            50: colors['mint-green'][50],
            100: colors['mint-green'][100],
            200: colors['mint-green'][200],
            250: colors['mint-green'][250],
            300: colors['mint-green'][300],
            400: colors['mint-green'][400],
            600: colors['mint-green'][600],
            700: colors['mint-green'][700],
            800: colors['mint-green'][800],
            950: colors['mint-green'][950],
          },
          orange: {
            50: colors.orange[50],
            100: colors.orange[100],
            200: colors.orange[200],
            300: colors.orange[300],
            350: colors.orange[350],
            400: colors.orange[400],
            500: colors.orange[500],
            600: colors.orange[600],
            700: colors.orange[700],
            800: colors.orange[800],
            850: colors.orange[850],
            900: colors.orange[900],
          },
          gray: {
            50: colors.gray[50],
            100: colors.gray[100],
          },
          customPrimary: {
            focusVisible: colors.customPrimary.focusVisible,
          },
        },
        typography: {
          htmlFontSize: 16,
          allVariants: {
            fontFamily: 'Inter, sans-serif',
          },
          h3: {
            fontSize: '3rem',
            fontWeight: 600,
            lineHeight: '3.5rem',
          },
          h4: {
            fontSize: '2.125rem',
            fontWeight: 600,
            lineHeight: '3rem',
          },
          h5: {
            fontSize: '1.5rem',
            fontWeight: 700,
            lineHeight: '2rem',
          },
          h6: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: '2rem',
          },
          body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: '1.25rem',
          },
          subtitle1: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: '1.75rem',
          },
          subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: '1.375rem',
          },
          button: {
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: '1.25rem',
          },
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            xsm: 632,
            md: 900,
            xmd: 1180,
            lg: 1280,
            xl: 1536,
          },
        },
      }),
    []
  );

  return <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>;
}
